import { render } from 'react-dom'
import Editor from 'ckeditor5-custom-build/build/ckeditor'
const { Plugin, Command, toWidget } = Editor.Dev

/* 
Per usare questo plugin serve creare una sottoclasse tipo questa:

class CkEditorPluginHtmlFormInput extends CkEditorPluginElementoReact {
  _getConfig() {
    return {
      tagHtml: 'input',
      render: CustomInput,
      nomeComandoInserisci: CkEditorPluginHtmlFormInput.getNomeComandoInserisci(),
    }
  }

  static getNomeComandoInserisci() {
    return 'input_inserisci'
  }
}

Se non serve il comando per inserire l'elemento dall'esterno, basta questa:

class CkEditorPluginHtmlFormInput extends CkEditorPluginElementoReact {
  _getConfig() {
    return {
      tagHtml: 'input',
      render: CustomInput,
    }
  }
}
*/

export default class CkEditorPluginElementoReact extends Plugin {
  init() {
    const {
      tagHtml,
      render,
      nomeComandoInserisci,
      nomeComandoFocus,
      nomeConfig,
    } = this._getConfig()

    this._tagHtml = tagHtml
    this._render = render
    this._tagModel = this._tagHtml + '__model'
    this._attr = this._tagHtml + '__attr'
    this._defineSchema()
    this._defineConverters()
    if (nomeComandoInserisci) {
      const comando = new ComandoInserisciElementoReact(this.editor)
      comando._tagModel = this._tagModel
      comando._attr = this._attr
      this.editor.commands.add(nomeComandoInserisci, comando)
    }
    if (nomeComandoFocus) {
      const comando = new ComandoFocusElementoReact(this.editor)
      comando._tagModel = this._tagModel
      comando._attr = this._attr
      this.editor.commands.add(nomeComandoFocus, comando)
    }
    if (nomeConfig) {
      const { propsElemento } = this.editor.config.get(nomeConfig)
      this._propsElemento = propsElemento || {}
    }
  }

  _defineSchema() {
    const schema = this.editor.model.schema

    schema.register(this._tagModel, {
      inheritAllFrom: '$inlineObject',
      allowIn: ['$root', '$container', '$htmlList', '$blockObject', '$inlineObject'],
      allowAttributes: [this._attr]
    })
  }

  _defineConverters() {
    const editor = this.editor
    const conversion = editor.conversion

    // Conversione da dati esterni a modello
    conversion.for('upcast').elementToElement({
      view: { name: this._tagHtml },
      model: (dataElement, { writer: modelWriter }) => {
        const attributi = creaJsonAttributi(this._attr, dataElement)
        return modelWriter.createElement(this._tagModel, attributi)
      }
    })

    // Conversione da modello a dati esterni
    conversion.for('dataDowncast').elementToElement({
      model: this._tagModel,
      view: (modelElement, { writer: dataWriter }) => {
        const attributi = leggiJsonAttributi(this._attr, modelElement)
        return dataWriter.createEmptyElement(this._tagHtml, attributi)
      }
    })

    // Conversione da modello a view
    conversion.for('editingDowncast').elementToElement({
      model: this._tagModel,
      view: (modelElement, { writer: viewWriter }) => {
        return this._renderElemento(modelElement, viewWriter)
      }
    }).add(dispatcher => {
      // Evento lanciato quando viene inserito/modificato/rimosso un elemento con attributo this._attr
      dispatcher.on('attribute:' + this._attr, (event, data, conversionApi) => {
        // Non fare niente se l'elemento è inserito/rimosso, solo se modificato
        if (!data.attributeOldValue || !data.attributeNewValue) return
        const modelElement = data.item
        // Rimuovi l'elemento corrente
        const viewElement = conversionApi.mapper.toViewElement(modelElement)
        conversionApi.writer.remove(viewElement)
        // Renderizza l'elemento modificato
        conversionApi.convertItem(modelElement)
      })
    })
  }

  _renderElemento(modelElement, viewWriter) {
    // Crea l'elemento contenitore (serve perché non si può inserire direttamente un raw element)
    const contenitore = viewWriter.createContainerElement('span')
    // Crea il wrapper come raw element e renderizza l'elemento react
    const attributiWrapperReact = { style: 'display: inline-block' }
    const wrapperReact = viewWriter.createRawElement('span', attributiWrapperReact, domElement => {
      const Componente = this._render
      const altreProps = this._propsElemento
      const attributi = leggiJsonAttributi(this._attr, modelElement)
      const props = {
        ...attributi,
        ...altreProps,
        modificaAttributi: nuoviAttributi => this._modificaAttributiElemento(modelElement, nuoviAttributi),
      }
      return render(<Componente {...props} />, domElement)
    })
    // Inserisci il wrapper nel contenitore
    viewWriter.insert(viewWriter.createPositionAt(contenitore, 0), wrapperReact)
    // Crea il widget completo
    return toWidget(contenitore, viewWriter)
  }

  _modificaAttributiElemento(modelElement, nuoviAttributi) {
    const model = this.editor.model
    model.change(modelWriter => {
      modelWriter.setAttribute(this._attr, JSON.stringify(nuoviAttributi), modelElement)
    })
  }
}



class ComandoInserisciElementoReact extends Command {
  execute(elementoHtmlString) {
    const model = this.editor.model
    const data = this.editor.data
    model.change(() => {
      // Processa stringa html
      const dataFragment = data.processor.toView(elementoHtmlString)
      // Inserisci nuovi elementi nel modello
      const modelFragment = data.toModel(dataFragment)
      model.insertContent(modelFragment)
    })
  }

  refresh() {
    const model = this.editor.model
    const selection = model.document.selection
    const allowedIn = model.schema.findAllowedParent(selection.getFirstPosition(), this._tagModel)
    this.isEnabled = allowedIn !== null
  }
}



class ComandoFocusElementoReact extends Command {
  execute(condizioneAttributi) {
    const model = this.editor.model
    const editingView = this.editor.editing.view
    const modelElement = findModelElement(model.document.getRoot(), modelElement => {
      if (modelElement.name === this._tagModel) {
        const attributi = leggiJsonAttributi(this._attr, modelElement)
        return condizioneAttributi(attributi)
      }
      return false
    })
    if (modelElement) {
      model.change(modelWriter => {
        const selection = modelWriter.createSelection(modelElement, 'on')
        modelWriter.setSelection(selection)
      })
      editingView.focus()
      editingView.scrollToTheSelection({ alignToTop: true, forceScroll: true })
    }
  }
}



function creaJsonAttributi(nomeAttr, dataElement) {
  const jsonGiaPresente = dataElement.getAttribute(nomeAttr)
  if (jsonGiaPresente) return { [nomeAttr]: jsonGiaPresente }

  const attributi = {}
  for (const [chiave, valore] of dataElement.getAttributes()) {
    attributi[chiave] = valore
  }
  return { [nomeAttr]: JSON.stringify(attributi) }
}

function leggiJsonAttributi(nomeAttr, modelElement) {
  return JSON.parse(modelElement.getAttribute(nomeAttr))
}



function findModelElement(modelElement, condition) {
  if (condition(modelElement)) return modelElement

  if (modelElement.getChildren) {
    for (const child of modelElement.getChildren()) {
      const found = findModelElement(child, condition)
      if (found) return found
    }
  }
}